import { yupResolver } from '@hookform/resolvers/yup';
import AddIcon from '@mui/icons-material/Add';
import { Grid, Paper, Stack, Box, Typography, DialogContent } from '@mui/material';
import { styled } from '@mui/material/styles';
import { isEmpty } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  ICandidate,
  IMappingFormSettingsResponse,
  ITimePeriod,
  updateCandidateDetails,
  UpdateCandidateDetails,
} from 'wisestep-api';
import {
  WsButton,
  WsTypography,
  WsTypoVariants,
  WsTypoColors,
  Label,
  ControlledTextInput,
  WsRadioGroup,
  WsLocationSearch,
  WsDatePicker,
  // SalaryDetails,
  WsSelect,
  ControlledLanguageInput,
  TagsInput,
  ControlledSalaryDetails,
  WsColors,
  formateDateInDateMonthYear,
  IUserDTO,
} from 'wisestep-base';
import * as yup from 'yup';

import WsCheckBox from '../../../../../components/WsCheckBox/WsCheckBox';
import { useUserContext } from '../../../../../context';
import useMappingFormSettings from '../../../../../hooks/useMappingFormSettings';
import { success, warning } from '../../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../../utils/NotificationStore';

import CertificationData, { Certification } from './components/CertificationData';
import ContactInfoCard from './components/ContactInfoCard';
import EducationData, { Education } from './components/EducationData';
import ExperienceData, { Experience } from './components/ExperienceData';
import { CountryOptions, visaOptionsByCountry } from './UpdateCandiadteDetailsHelper';
interface EditTextWithLabelProps {
  name: string;
  labelText?: string;
  required?: boolean;
  type?: string;
  value?: string[] | null;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const EditTextWithLabel = ({ name, labelText, required, type }: EditTextWithLabelProps) => (
  <Grid item xs={8}>
    <ControlledTextInput
      name={name}
      type={type ? type : 'text'}
      labelText={labelText}
      variant="outlined"
      fullWidth
      required={required}
    />
  </Grid>
);

export interface WsRadioObj {
  label: string;
  value: string | number;
}
export interface optionsVisa {
  label: string;
  value: string;
  additionalInfo: string;
}

interface EditRadioProps {
  selectArray: WsRadioObj[];
  name: string;
  label: string;
}
const allowedContractTypes = [
  'Contract',
  'Temporary',
  'Internship',
  'Freelance',
  'ContractToHireW2',
  'ContractW2',
  'ContractIndependent',
  'ContractToHireIndependent',
  'ContractCorpToCorp',
  'ContractToHireCorpToCorp',
  'PartTime',
  'FixedTermContract',
].map((val) => {
  return {
    label: val,
    value: val,
  };
});

const radioButtonsData = [
  {
    label: 'Current Job Type',
    name: 'currentJobType',
    selectArray: allowedContractTypes,
  },
  {
    label: 'Job Includes Travel',
    name: 'jobIncludesTravel',
    selectArray: [
      { label: 'Yes', value: 'true' },
      { label: 'No', value: 'false' },
    ],
  },
  {
    label: 'Willing to Relocate',
    name: 'willingToRelocate',
    selectArray: [
      { label: 'Yes', value: 'true' },
      { label: 'No', value: 'false' },
    ],
  },
  {
    label: 'Family Willing To Relocate',
    name: 'familyWillingToRelocate',
    selectArray: [
      { label: 'Yes', value: 'true' },
      { label: 'No', value: 'false' },
    ],
  },
  {
    label: 'Notice Period',
    name: 'noticePeriod',
    selectArray: [
      { label: 'Immediately', value: 'Immediate' },
      { label: '15 Days', value: 'Within_15_Days' },
      { label: '1 month', value: 'Within_1_Month' },
      { label: '2 months', value: 'Within_2_Months' },
      { label: '3 months', value: 'Within_3_Months' },
      { label: '4 months', value: 'Within_4_Months' },
      { label: 'None', value: 'None' },
    ],
  },
  {
    label: 'Interviewing With Others',
    name: 'interviewingWithOthers',
    selectArray: [
      { label: 'Yes', value: 'true' },
      { label: 'No', value: 'false' },
    ],
  },
];

const FormTagsInput = styled(TagsInput)(() => ({
  '.MuiOutlinedInput-root': {
    padding: '4.5px 8px',
    margin: 0,
  },
}));

export const EditRadioButton = ({ name, selectArray, label }: EditRadioProps) => (
  <Grid item xs={12}>
    <Label>{label}</Label>
    <WsRadioGroup name={name} row={false} selectArray={selectArray} />
  </Grid>
);

export interface CandidateDetailsCardProps {
  title: string;
  description: string;
  candidate: ICandidate;
  updateCandidate?: (data: ICandidate) => void;
  skillDetails?: string[];
  isSkills?: boolean;
  showMissingInfo?: boolean;
  onChange?: (index: number) => void;
  paperRefs: React.MutableRefObject<HTMLElement | undefined>;
  close: () => void;
}

const GenderOptions = [
  {
    label: 'Male',
    value: 'MALE',
  },
  {
    label: 'Female',
    value: 'FEMALE',
  },
  {
    label: 'Not Specified',
    value: 'NOT_SPECIFIED',
  },
];

const visaValidityOptions = [
  {
    label: 'Yes',
    value: 'Yes',
  },
  {
    label: 'No',
    value: 'No',
  },
];

const schema = yup.object().shape({
  education: yup
    .array()
    .nullable()
    .of(
      yup.object().shape({
        org: yup.string().nullable().required('Please add institution name'),
      })
    ),
  candidateCertificationsData: yup
    .array()
    .nullable()
    .of(
      yup.object().shape({
        name: yup.string().nullable().required('Please add certification name'),
      })
    ),
  experience: yup
    .object()
    .nullable()
    .shape({
      history: yup.array().of(
        yup.object().shape({
          org: yup.string().nullable().required('Please add organization name'),
        })
      ),
    }),
});
const UpdateCandidateDetailsCard = ({
  onChange = () => {},
  close,
  paperRefs,
  candidate,
  updateCandidate,
}: CandidateDetailsCardProps) => {
  const loggedInUser = useUserContext().userDTO as IUserDTO;

  const setNotification = useNotificationStore((state) => state.setNotification);
  const methods = useForm({
    defaultValues: {
      ...candidate,
      candidateId: candidate.candidateId ?? candidate.id,
      currentSalary: {
        value: candidate?.currentSalary,
        currency: candidate?.currentSalaryCurrency,
        timeSpan: candidate?.currentSalaryTimeSpan ?? null,
      },
      expectedSalary: {
        value: candidate?.expectedBaseSalary,
        currency: candidate?.expectedSalaryCurrency,
        timeSpan: candidate?.expectedBaseSalaryTimeSpan ?? null,
      },
      candidateLanguages: candidate?.candidateLanguages ?? [],
      source: candidate?.sources?.[0] ?? '',
      workAuthorization: candidate?.workAuthorization ?? '',
      visaStatus: candidate?.visaStatus ?? '',
      visaValidityOptions: !isEmpty(candidate?.visaValidity) ? 'Yes' : 'No',
      workAuthorizationCategory: '',
      visaStatusOther: candidate?.visaStatusOther ?? '',
    },
    resolver: yupResolver(schema),
  });
  console.log('candidate value', candidate);
  const { watch } = methods;
  const [options, setOptions] = useState<optionsVisa[]>([]);
  const mappingFormSettingsResponse = useMappingFormSettings();
  const [mappingFormSettings, setMappingFormSetings] = useState<
    IMappingFormSettingsResponse | undefined
  >();
  useEffect(() => {
    if (mappingFormSettingsResponse.fields)
      setMappingFormSetings(mappingFormSettingsResponse.fields);
  }, [mappingFormSettingsResponse.fields]);

  const [educationData, setEducationCollapsibleBoxes] = useState<Education[]>(
    candidate?.education ?? []
  );
  const [hideEducationAdd, setHideEducationHide] = useState<boolean>(false);
  const [hideCertificateAdd, setHideCertificateHide] = useState<boolean>(false);
  const [hideExperienceAdd, setHideExperienceHide] = useState<boolean>(false);
  const getVisaOptionsForCountry = (countryValue) => {
    if (!countryValue) return [];

    const countryOptions = visaOptionsByCountry.find(
      (entry) => entry.country.toUpperCase() === countryValue.toUpperCase()
    );
    countryOptions && setOptions(countryOptions?.options);
    return countryOptions
      ? countryOptions.options.map(({ label, value }) => ({ label, value }))
      : [];
  };

  const [certificationCollapsibleBoxes, setCertificationCollapsibleBoxes] = useState<
    Certification[]
  >(candidate?.candidateCertificationsData ?? []);
  const [experienceHistoryData, setExperienceHistoryData] = useState<Experience[]>(
    candidate?.experience?.history ?? []
  );
  const selectedCountry = watch('workAuthorization');
  const selectedVisaOption = watch('visaStatus');
  const selectedVisaValidity = watch('visaValidityOptions');
  const visaOption = useMemo(() => {
    if (!selectedCountry || selectedCountry === 'NOT_SPECIFIED') {
      return [];
    }
    return getVisaOptionsForCountry(selectedCountry);
  }, [selectedCountry]);
  const isOtherVisaOptionSelected = selectedVisaOption === 'Other';
  const isVisaValidityyes = selectedVisaValidity === 'Yes';

  const getTimePeriod = (date: Date | null | undefined): ITimePeriod | null => {
    if (date) {
      return {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
      };
    }
    return null;
  };
  const educationAddCollapsibleBox = () => {
    const data = [
      ...educationData,
      {
        org: '',
        degree: '',
        grade: '',
        expanded: true,
        isAdd: true,
        startDate: null,
        endDate: null,
        major: '',
        location: '',
        description: '',
      },
    ];
    setEducationCollapsibleBoxes(data);
    setHideEducationHide(true);
    methods.setValue('education', data);
  };
  const removeEducationAddCollapsibleBox = (index: number) => {
    const updatedData = methods.getValues('education') ?? [];
    const education = educationData[index];
    const educationDetails = updatedData.filter((_, indexNumber) => indexNumber !== index);
    setEducationCollapsibleBoxes([...educationDetails]);
    if (education.isAdd) {
      setHideEducationHide(false);
    }
    methods.setValue('education', educationDetails);
  };
  const confirmEducationAddCollapsibleBox = async (index: number) => {
    const isValid = await methods.trigger('education');
    if (!isValid) {
      return;
    }
    const updatedData = methods.getValues('education') ?? [];

    const education: Education[] = [...updatedData];
    education[index].isAdd = false;
    education[index].expanded = false;
    education[index].startDate = getTimePeriod(education[index].start);
    education[index].endDate = getTimePeriod(education[index].end);
    setEducationCollapsibleBoxes([...education]);
    setHideEducationHide(false);
    methods.setValue('education', education);
  };

  const certifcationAddCollapsibleBox = () => {
    const data = [
      ...certificationCollapsibleBoxes,
      {
        name: '',
        url: '',
        licenseNumber: '',
        company: '',
        description: '',
        expanded: true,
        startDate: null,
        endDate: null,
        score: 0,
        isExpiry: false,
        isAdd: true,
      },
    ];
    setCertificationCollapsibleBoxes(data);
    setHideCertificateHide(true);
    methods.setValue('candidateCertificationsData', data);
  };
  const removeCertification = (index: number) => {
    const updatedData = methods.getValues('candidateCertificationsData') ?? [];
    const certification = certificationCollapsibleBoxes[index];
    const CertificationData = updatedData.filter((_, indexNumber) => indexNumber !== index);
    setCertificationCollapsibleBoxes([...CertificationData]);
    if (certification.isAdd) {
      setHideCertificateHide(false);
    }
    methods.setValue('candidateCertificationsData', CertificationData);
  };

  const confirmCertificationAddCollapsibleBox = async (index: number) => {
    const isValid = await methods.trigger('candidateCertificationsData');
    if (!isValid) {
      return;
    }

    const updatedData = methods.getValues('candidateCertificationsData') ?? [];
    const certification: Certification[] = [...updatedData];
    certification[index].isAdd = false;
    certification[index].expanded = false;
    certification[index].startDate = getTimePeriod(certification[index].start);
    certification[index].endDate = getTimePeriod(certification[index].end);
    setCertificationCollapsibleBoxes([...certification]);
    setHideCertificateHide(false);
    methods.setValue('candidateCertificationsData', certification);
  };

  const addExperiencHistory = () => {
    const data = [
      ...experienceHistoryData,
      {
        org: '',
        title: '',
        periodEnd: null,
        periodStart: null,
        isPrimary: false,
        isAdd: true,
        expanded: true,
      },
    ];
    setExperienceHistoryData(data);
    setHideExperienceHide(true);
    methods.setValue('experience.history', data);
  };
  const removeExperienceHistory = (index: number) => {
    const updatedData = methods.getValues('experience.history') ?? [];
    const experience = experienceHistoryData[index];
    const experienceDetails = updatedData.filter((_, indexNumber) => indexNumber !== index);
    setExperienceHistoryData([...experienceDetails]);
    if (experience.isAdd) {
      setHideExperienceHide(false);
    }
    methods.setValue('experience.history', experienceDetails);
  };
  const confirmExperienceHistory = async (index: number) => {
    const isValid = await methods.trigger('experience.history');
    if (!isValid) {
      return;
    }
    const updatedData = methods.getValues('experience.history') ?? [];

    const experience: Experience[] = [...updatedData];
    experience[index].isAdd = false;
    experience[index].expanded = false;
    experience[index].periodStart = getTimePeriod(experience[index].startDate);
    experience[index].periodEnd = getTimePeriod(experience[index].endDate);
    setExperienceHistoryData([...experience]);
    setHideExperienceHide(false);
    methods.setValue('experience.history', experience);
  };

  const formatDateRange = (fromJoiningDate, fromEndDate) => {
    const fromDate = fromJoiningDate ? new Date(fromJoiningDate) : null;
    const toDate = fromEndDate ? new Date(fromEndDate) : null;

    const formatteddate = fromDate
      ? fromDate.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : '';

    const formattedEndDate = toDate
      ? toDate.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : '';

    return `${formatteddate} - ${formattedEndDate}`;
  };

  const onFormSubmit = async (data) => {
    if (
      isOtherVisaOptionSelected &&
      (!data.visaStatusOther || data.visaStatusOther.trim() === '')
    ) {
      setNotification(warning('Please provide details for Other Visa Status'));
      return;
    }

    const currentSalaryData = data.currentSalary;

    if (currentSalaryData && typeof currentSalaryData !== 'number') {
      data.currentSalary = currentSalaryData.value;
      data.currentSalaryCurrency = currentSalaryData.currency;
      data.currentSalaryTimeSpan = currentSalaryData.timeSpan ?? null;
    } else {
      data.currentSalaryTimeSpan = null;
    }
    const salaryData = data.expectedSalary;

    if (salaryData && typeof data.expectedSalary !== 'number') {
      data.expectedBaseSalary = salaryData.value;
      data.expectedSalaryCurrency = salaryData.currency;
      data.expectedBaseSalaryTimeSpan = salaryData.timeSpan ?? null;
    } else {
      data.expectedBaseSalaryTimeSpan = null;
    }
    const currentJob = data?.experience?.current ?? {};
    const expHistory = data?.experience?.history ?? [];
    if (expHistory) {
      if (!data.experience) {
        data.experience = {};
      }
      data.experience.history = expHistory.map((exp) => {
        return {
          ...exp,
          periodStart: getTimePeriod(exp.startDate),
          periodEnd: getTimePeriod(exp.endDate),
        };
      });
    }

    if (!data?.previousEmployers || expHistory.length !== data?.previousEmployers?.length) {
      data.previousEmployers = expHistory.map((exp) => exp.org);
      data.previousJobTitles = expHistory.map((exp) => exp.title);
    }
    if (!data.currentJobTitle && currentJob) {
      data.currentJobTitle = currentJob.title;
    }
    if (!data.currentEmployer && currentJob) {
      data.currentEmployer = currentJob.org;
    }
    if (!data.candidateCertificationsData) {
      data.candidateCertificationsData = [];
    } else {
      data.candidateCertificationsData = data.candidateCertificationsData.map((cert) => {
        return {
          ...cert,
          startDate: getTimePeriod(cert.start),
          endDate: getTimePeriod(cert.end),
        };
      });
    }
    if (!data.education) {
      data.education = [];
    } else {
      data.education = data.education.map((edu) => {
        return {
          ...edu,
          startDate: getTimePeriod(edu.start),
          endDate: getTimePeriod(edu.end),
        };
      });
    }
    if (!data.currentJobType || data.currentJobType === '') {
      data.currentJobType = null;
    }
    if (!data.noticePeriod || data.noticePeriod === '') {
      data.noticePeriod = null;
    }
    const selectedOption =
      options && options?.find((option) => option.label === selectedVisaOption);
    const additionalInfo = selectedOption
      ? selectedOption?.additionalInfo
      : 'No additional info available';
    data.visaValidity = formateDateInDateMonthYear(data.visaValidity);
    const newdata = { ...data, workAuthorizationCategory: additionalInfo };
    const candidateDetails: UpdateCandidateDetails = {
      candidate: newdata,
      isSingleEdit: false,
      field: '',
    };

    try {
      candidateDetails.candidate.recruiterId = loggedInUser.email ?? '';
      await updateCandidateDetails(candidateDetails);
      setNotification(success(`Candidate updated Sucessfully`));
      !updateCandidate && window.location.reload();
      updateCandidate && updateCandidate(candidateDetails.candidate);
      close();
    } catch {
      setNotification(warning('Something went wrong, Please try again.'));
    }
  };
  const onError = (errors) => {
    const error = errors['education'] ??
      errors['candidateCertificationsData'] ??
      errors['experience'] ?? [
        {
          name: {
            message: 'Please fill all mandatory fields',
          },
        },
      ];
    const message = error[0].name ?? error[0].org;
    setNotification(warning(message.message));
  };
  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onFormSubmit, onError)}>
        <Box sx={{ maxHeight: '100%', overflow: 'auto' }} ref={paperRefs}>
          <Paper
            variant="outlined"
            sx={{ borderRadius: 2, p: 2, mt: 2 }}
            component="form"
            role="presentation"
            onMouseEnter={() => onChange(0)}
            // elevation={tabIndex === 0 ? 3 : 0}
          >
            <Typography variant="h5">{'Personal and Contact Information'}</Typography>
            <WsTypography variant={WsTypoVariants.CAPTION} color={WsTypoColors.GREYTEXT}>
              {'Manage personal details, contant info and visa details.'}
            </WsTypography>
            <DialogContent>
              <Grid container spacing={2}>
                <ContactInfoCard candidate={candidate} />
                <Grid item xs={8}>
                  <WsSelect
                    name="workAuthorization"
                    label="Authorized Country to Work"
                    selectArray={CountryOptions}
                    defaultValue={candidate?.workAuthorization ?? ''}
                  />
                </Grid>
                <Grid item xs={8}>
                  <WsSelect
                    name="visaStatus"
                    label="Visa Options"
                    selectArray={visaOption}
                    disabled={!selectedCountry || selectedCountry === 'NOT_SPECIFIED'}
                    defaultValue={candidate?.visaStatus ?? ''}
                  />
                </Grid>
                {isOtherVisaOptionSelected && (
                  <EditTextWithLabel
                    name="visaStatusOther"
                    labelText="Visa Option Other Note"
                    required={true}
                  />
                )}
                <Grid item xs={8}>
                  <WsSelect
                    name="visaValidityOptions"
                    label="Visa Validity Available"
                    selectArray={visaValidityOptions}
                    defaultValue={!isEmpty(candidate?.visaValidity) ? 'Yes' : 'No'}
                  />
                </Grid>
                {isVisaValidityyes && (
                  <Grid item xs={8}>
                    <Label>Visa Validity till</Label>
                    <WsDatePicker
                      name="visaValidity"
                      label="Visa Validity"
                      disableFuture={false}
                      disabled={false}
                      required={false}
                    />
                  </Grid>
                )}
                <EditTextWithLabel name="nationality" labelText="Nationality" required={true} />
                <Grid item xs={8}>
                  <Label>Date of Birth</Label>
                  <WsDatePicker
                    name="dob"
                    label="Date of Birth"
                    disableFuture={true}
                    disabled={false}
                    required={false}
                  />
                </Grid>
                <Grid item xs={8}>
                  <WsLocationSearch
                    name="currentLocation"
                    required={false}
                    placeHolder="Current Location"
                    multiSelect={false}
                  />
                </Grid>
                <Grid item xs={8}>
                  <WsSelect
                    name="gender"
                    label="Gender"
                    selectArray={GenderOptions}
                    defaultValue="NOT SPECIFIED"
                  />
                </Grid>
              </Grid>
            </DialogContent>
          </Paper>
          <Paper
            variant="outlined"
            sx={{ borderRadius: 2, p: 2, mt: 2 }}
            component="form"
            role="presentation"
            onMouseEnter={() => onChange(1)}
            // elevation={tabIndex === 1 ? 3 : 0}
          >
            <Typography variant="h5">{'Job Preference And Availability'}</Typography>
            <WsTypography variant={WsTypoVariants.CAPTION} color={WsTypoColors.GREYTEXT}>
              {'Manage job, location and availability preferences.'}
            </WsTypography>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12} display="flex" flexDirection={'column'}>
                  <Label>{'Job Preference'}</Label>
                  <WsCheckBox name="jobPreferencePermanent" label="Permanent" bold={false} />
                  <WsCheckBox name="jobPreferenceContract" label="Contract" bold={false} />
                </Grid>

                {radioButtonsData.map(({ label, name, selectArray }) => (
                  <EditRadioButton key={name} label={label} name={name} selectArray={selectArray} />
                ))}
              </Grid>
            </DialogContent>
          </Paper>
          <Paper
            variant="outlined"
            sx={{ borderRadius: 2, p: 2, mt: 2 }}
            component="form"
            role="presentation"
            onMouseEnter={() => onChange(2)}
            // elevation={tabIndex === 2 ? 3 : 0}
          >
            <Typography variant="h5">{'Compensation and Benefits'}</Typography>
            <WsTypography variant={WsTypoVariants.CAPTION} color={WsTypoColors.GREYTEXT}>
              {'Manage job, location and availability preferences.'}
            </WsTypography>
            <DialogContent>
              <Grid container spacing={2}>
                {mappingFormSettings?.currentSalary.show && (
                  <Grid item xs={10}>
                    <Label>{'Current Salary'}</Label>
                    <ControlledSalaryDetails name="currentSalary" />
                  </Grid>
                )}

                {mappingFormSettings?.otherAllowance.show && (
                  <Grid item xs={10}>
                    <ControlledTextInput
                      name="otherCurrentAllowances"
                      labelText={'Other allowances and benefits'}
                      fullWidth
                      id="other_allowance"
                      placeholder="Enter other allowances and benefits"
                      variant="outlined"
                      autoComplete="other_allowance"
                      required={mappingFormSettings?.otherAllowance.mandatory}
                    />
                  </Grid>
                )}
                {mappingFormSettings?.expectedSalary.show && (
                  <Grid item xs={10}>
                    <Label>{'Expected Salary'}</Label>
                    <ControlledSalaryDetails name="expectedSalary" />
                  </Grid>
                )}
                {mappingFormSettings?.expectedAllowance.show && (
                  <Grid item xs={10}>
                    <ControlledTextInput
                      name="otherExpectedAllowances"
                      labelText={'Expected allowances and benefits'}
                      fullWidth
                      id="expected_allowance"
                      placeholder="Enter expected allowances and benefits"
                      variant="outlined"
                      autoComplete="expected_allowance"
                      required={mappingFormSettings?.expectedAllowance.mandatory}
                    />
                  </Grid>
                )}
              </Grid>
            </DialogContent>
          </Paper>
          <Paper
            variant="outlined"
            sx={{ borderRadius: 2, p: 2, mt: 2 }}
            component="form"
            role="presentation"
            onMouseEnter={() => onChange(3)}
            // elevation={tabIndex === 3 ? 3 : 0}
          >
            <Typography variant="h5">{'Skills'}</Typography>
            <WsTypography variant={WsTypoVariants.CAPTION} color={WsTypoColors.GREYTEXT}>
              {'Manage skills parsed through CV'}
            </WsTypography>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <FormTagsInput
                    placeholder="Enter Skills"
                    multiple={true}
                    initialValue={methods.getValues('skills') ?? []}
                    name="skills"
                    size="small"
                  />
                </Grid>
              </Grid>
            </DialogContent>
          </Paper>
          <Paper
            variant="outlined"
            sx={{ borderRadius: 2, p: 2, mt: 2 }}
            component="form"
            role="presentation"
            onMouseEnter={() => onChange(4)}
            // elevation={tabIndex === 4 ? 4 : 0}
          >
            <Typography variant="h5">{'Other Information'}</Typography>
            <WsTypography variant={WsTypoVariants.CAPTION} color={WsTypoColors.GREYTEXT}>
              {'Manage preference, tags, languages and documents ID details'}
            </WsTypography>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={8} display="flex" flexDirection={'column'}>
                  <Label>{'Communication Preference'}</Label>
                  <WsCheckBox name="commPreferenceEmail" label="Email" bold={false} />
                  <WsCheckBox name="commPreferencePhone" label="Phone" bold={false} />
                </Grid>

                <Grid item xs={8}>
                  <Label>Tags</Label>
                  <FormTagsInput
                    name="tags"
                    placeholder="Enter tags"
                    multiple={true}
                    initialValue={[]}
                  />
                </Grid>
                {/* <EditTextWithLabel name="tags" labelText="tags" type="text" /> */}
                <EditTextWithLabel name="source" labelText="Source" />
                <Grid item xs={8}>
                  <ControlledLanguageInput
                    name="candidateLanguages"
                    initialValue={[{ languageName: '', fluencyLevel: '' }]}
                  />
                </Grid>
                <EditTextWithLabel name="panCard" labelText="PAN Card" type="text" />
                <Grid item xs={8}>
                  <ControlledTextInput
                    name="passportNumber"
                    labelText={`Passport Number`}
                    fullWidth
                    id="passportNumber"
                    placeholder="Enter Passport Number"
                    variant="outlined"
                    autoComplete="passportNumber"
                  />
                </Grid>
                <Grid item xs={8}>
                  <ControlledTextInput
                    name="passportType"
                    labelText={`Passport Type`}
                    fullWidth
                    id="passportType"
                    placeholder="Enter Passport Type"
                    variant="outlined"
                    autoComplete="passportType"
                  />
                </Grid>
                <EditTextWithLabel name="ssnNumber" labelText="SSN Number" type="text" />
                {/* <EditTextWithLabel name="candidateId" labelText="Candidate ID" type="text" /> */}
              </Grid>
            </DialogContent>
          </Paper>
          <Paper
            variant="outlined"
            sx={{ borderRadius: 2, p: 2, mt: 2 }}
            component="form"
            role="presentation"
            onMouseEnter={() => onChange(5)}
            // elevation={tabIndex === 5 ? 5 : 0}
          >
            <Typography variant="h5">{'Experience'}</Typography>
            <WsTypography variant={WsTypoVariants.CAPTION} color={WsTypoColors.GREYTEXT}>
              {'Manage experience related details'}
            </WsTypography>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <>
                    <>
                      <Grid>
                        <Label>{'Total experience'}</Label>
                      </Grid>
                      <Grid container>
                        <Grid item xs={6}>
                          <Grid>
                            <ControlledTextInput
                              name="totalYearsExperience"
                              type="number"
                              fullWidth
                              id="candidate_exp_in_year"
                              placeholder={'Enter Years'}
                              variant="outlined"
                              autoComplete="candidate_exp_in_year"
                              required={false}
                            />
                          </Grid>
                        </Grid>
                        <Grid item xs={6}>
                          <Grid>
                            <ControlledTextInput
                              name="totalMonthsExperience"
                              type="number"
                              fullWidth
                              id="candidate_exp_in_month"
                              placeholder="Enter Month"
                              variant="outlined"
                              autoComplete="candidate_exp_in_month"
                              required={false}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </>

                    <>
                      <Grid>
                        <Label>{'Relevant experience'}</Label>
                      </Grid>
                      <Grid container>
                        <Grid item xs={6}>
                          <Grid>
                            <ControlledTextInput
                              name="relevantYearsExperience"
                              type="number"
                              fullWidth
                              id="candidate_exp_in_year"
                              placeholder="Enter Years"
                              variant="outlined"
                              autoComplete="candidate_exp_in_year"
                              required={false}
                            />
                          </Grid>
                        </Grid>
                        <Grid item xs={6}>
                          <Grid>
                            <ControlledTextInput
                              name="relevantMonthsExperience"
                              type="number"
                              fullWidth
                              id="candidate_exp_in_month"
                              placeholder="Enter Month"
                              variant="outlined"
                              autoComplete="candidate_exp_in_month"
                              required={false}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </>
                    <Grid item xs={12} my={2}>
                      <Label>{'Current experience'}</Label>
                    </Grid>
                    {candidate?.experience && (
                      <ExperienceData
                        experience={{ ...candidate.experience.current, isPrimary: true }}
                        formatDateRange={formatDateRange}
                      />
                    )}

                    <Grid item xs={12} my={2}>
                      <Label>{'Experience History'}</Label>
                    </Grid>
                    {experienceHistoryData?.map((experience, index) => (
                      <ExperienceData
                        experience={experience}
                        index={index}
                        formatDateRange={formatDateRange}
                        key={index}
                        onDelete={() => removeExperienceHistory(index)}
                        onConfirm={() => confirmExperienceHistory(index)}
                      />
                    ))}
                    <Grid item xs={8} sx={{ mt: 3, mb: 3 }}>
                      {!hideExperienceAdd && (
                        <WsButton
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={addExperiencHistory}
                        >
                          Add Experience
                        </WsButton>
                      )}
                    </Grid>
                    <Grid>
                      <ControlledTextInput
                        name="currentJobTitle"
                        labelText={'Current Job Title'}
                        fullWidth
                        id="candidate_jobTitle"
                        placeholder="Enter Current Job Title"
                        variant="outlined"
                        autoComplete="candidate_jobTitle"
                        required={false}
                      />
                    </Grid>

                    <Grid>
                      <ControlledTextInput
                        name="currentEmployer"
                        labelText={'Current Employer'}
                        fullWidth
                        id="candidate_currentCompany"
                        placeholder="Enter Current Employer"
                        variant="outlined"
                        autoComplete="candidate_currentCompany"
                        required={false}
                      />
                    </Grid>

                    <Grid>
                      <Label>{'Previous Job Titles'}</Label>
                      <FormTagsInput
                        name="previousJobTitles"
                        placeholder="Enter previous job titles"
                        multiple={true}
                        initialValue={[]}
                        required={false}
                      />
                    </Grid>

                    <Grid>
                      <Label>{'Previous Employers'}</Label>
                      <FormTagsInput
                        name="previousEmployers"
                        placeholder="Enter previous employers"
                        multiple={true}
                        initialValue={[]}
                        required={false}
                      />
                    </Grid>
                  </>
                </Grid>
              </Grid>
            </DialogContent>
          </Paper>
          <Paper
            variant="outlined"
            sx={{ borderRadius: 2, p: 2, mt: 2 }}
            component="form"
            role="presentation"
            onMouseEnter={() => onChange(6)}
            // elevation={tabIndex === 6 ? 6 : 0}
          >
            <Typography variant="h5">{'Education'}</Typography>
            <WsTypography variant={WsTypoVariants.CAPTION} color={WsTypoColors.GREYTEXT}>
              {'Manage education related details'}
            </WsTypography>

            {educationData?.map((box, index) => (
              <EducationData
                box={box}
                index={index}
                formatDateRange={formatDateRange}
                key={index}
                onDelete={() => removeEducationAddCollapsibleBox(index)}
                onConfirm={() => confirmEducationAddCollapsibleBox(index)}
              />
            ))}
            <Grid item xs={8} sx={{ my: 3 }}>
              {!hideEducationAdd && (
                <WsButton
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={educationAddCollapsibleBox}
                >
                  Add Education
                </WsButton>
              )}
            </Grid>
            <Grid item xs={8}>
              <Label>{'Highest education qualifications'}</Label>
              <FormTagsInput
                name="educationQualifications"
                placeholder="Enter qualifications"
                multiple={true}
                initialValue={[]}
                required={false}
              />
            </Grid>
          </Paper>
          <Paper
            variant="outlined"
            sx={{ borderRadius: 2, p: 2, mt: 2, mb: 4 }}
            component="form"
            role="presentation"
            onMouseEnter={() => onChange(6)}
            // elevation={tabIndex === 6 ? 6 : 0}
          >
            <Typography variant="h5">{'Certification'}</Typography>
            <WsTypography variant={WsTypoVariants.CAPTION} color={WsTypoColors.GREYTEXT}>
              {'Manage certification related details'}
            </WsTypography>
            {certificationCollapsibleBoxes.map((box, index) => (
              <CertificationData
                box={box}
                index={index}
                formatDateRange={formatDateRange}
                key={index}
                onDelete={() => removeCertification(index)}
                onConfirm={() => confirmCertificationAddCollapsibleBox(index)}
              />
            ))}
            <Grid item xs={8} sx={{ mt: 3 }}>
              {!hideCertificateAdd && (
                <WsButton
                  variant="outlined"
                  onClick={certifcationAddCollapsibleBox}
                  startIcon={<AddIcon />}
                >
                  Add Certification
                </WsButton>
              )}
            </Grid>
          </Paper>
        </Box>
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          sx={{
            borderTop: `1px solid ${WsColors.TertiaryShades200}`,
            position: 'fixed',
            bottom: 0,
            zIndex: 3,
            width: '-webkit-fill-available',
            bgcolor: 'white',
            height: '4rem',
            gap: '1rem',
            pr: 2,
          }}
        >
          <WsButton size="medium" variant="outlined" onClick={close}>
            Discard changes
          </WsButton>
          <WsButton type="submit" size="medium" variant="contained">
            Save
          </WsButton>
        </Stack>
      </form>
    </FormProvider>
  );
};

export default UpdateCandidateDetailsCard;
